import { CircularProgress, makeStyles, TextField } from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { useEffect, useState } from "react";
import Operarios from "src/services/operarios"; // Usamos solo datos en vivo del backend; sin fallback estático

interface Operario {
  id: string; // id o legajo normalizado
  usuario: string; // nombre normalizado
  cargo: string;
}

interface AutocompletePropsOp {
  className?: string;
  label: string;
  value: Operario | null;
  setValue: (usuario: Operario | null) => void;
  error?: boolean;
  disabled?: boolean;
  filterCargo?: string | null; // opcional: filtrar por cargo exacto; null para traer todos
  tipoEvaluado?: "OPERARIO" | "SUPERVISOR" | null; // opcional: modo evaluación (backend filtra por tipo + antigüedad si aplica)
}

const useStyles = makeStyles({
  option: { color: "#404040", fontWeight: "normal" },
});

const Autocompleteoperario: React.FC<AutocompletePropsOp> = (
  props: AutocompletePropsOp
) => {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<Operario[]>([]);
  const [allFiscales, setAllFiscales] = useState<Operario[]>([]); // Caché completo
  const [value, setValue] = useState<Operario | null>(props.value);
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false); // trackear apertura del dropdown para poder listar sin tipear
  const [defaultOptionsEvaluacion, setDefaultOptionsEvaluacion] = useState<Operario[]>([]); // lista inicial (cache) para modo evaluación
  const classes = useStyles();

  useEffect(() => {
    if (props.value !== value) setValue(props.value);
  }, [props.value]);

  useEffect(() => {
    setInput(value ? `${value.usuario} (${value.id})` : ""); // mostrar nombre + legajo en el input para trazabilidad
  }, [value]);

  // Si cambia el tipo de evaluado, invalidar la lista inicial cacheada
  useEffect(() => {
    if (!props.tipoEvaluado) return;
    setDefaultOptionsEvaluacion([]);
  }, [props.tipoEvaluado]);

  // Requerimiento UX: al tocar/abrir el campo en modo evaluación, mostrar lista inicial sin escribir
  useEffect(() => {
    if (!props.tipoEvaluado) return; // solo aplica en modo evaluación
    if (props.disabled) return; // si está deshabilitado, no consultar
    if (!open) return; // solo consultar cuando el dropdown está abierto
    const texto = (input || "").trim(); // normalizar texto
    if (texto.length > 0) return; // si el usuario está escribiendo, se maneja por el efecto de búsqueda

    if (defaultOptionsEvaluacion.length) {
      setOptions(defaultOptionsEvaluacion); // reutilizar cache para respuesta inmediata
      return;
    }

    let activo = true; // evitar setState si cambia el ciclo de vida
    setLoading(true);
    Operarios.listarEvaluables({ tipo: props.tipoEvaluado }) // sin q: listar inicial filtrado por backend
      .then((arr: any[]) => {
        if (!activo) return;
        const normalizados = (arr || [])
          .map((o: any) => ({
            id: String(o.legajo ?? o.id ?? ""),
            usuario: String(o.nombre ?? o.usuario ?? ""),
            cargo: String(o.cargo ?? ""),
          }))
          .filter((o: Operario) => o.id && o.usuario)
          .slice(0, 50);
        setDefaultOptionsEvaluacion(normalizados);
        setOptions(normalizados);
      })
      .catch(() => {
        if (!activo) return;
        setOptions([]);
      })
      .finally(() => {
        if (!activo) return;
        setLoading(false);
      });

    return () => {
      activo = false;
    };
  }, [open, input, props.tipoEvaluado, props.disabled, defaultOptionsEvaluacion]);

  // Precargar opciones al montar con estrategia según cargo
  useEffect(() => {
    setLoading(true); // Activamos loading mientras cargamos desde backend

    // si estamos en modo evaluación, no precargamos todo; el backend filtra por búsqueda (q)
    if (props.tipoEvaluado) { // usar búsqueda remota en lugar de precarga masiva
      setAllFiscales([]); // limpiar caché local
      setOptions([]); // iniciar sin opciones hasta que el usuario busque
      setLoading(false); // no mantener spinner sin actividad
      return; // salir del efecto
    }

    const cargoFiltro = (props.filterCargo ?? 'FISCALIZADOR A').toUpperCase().trim(); // determinar filtro deseado

    const cargar = async () => { // función para cargar opciones
      try {
        if (cargoFiltro === 'FISCALIZADOR A') { // comportamiento original
          const arr = await Operarios.listarFiscalesJefatura(); // obtener fiscales
          if (Array.isArray(arr) && arr.length) {
            const fiscales = arr.map((o: any) => ({
              id: String(o.id),
              usuario: String(o.nombre),
              cargo: String(o.cargo || 'FISCALIZADOR A'),
            }));
            setAllFiscales(fiscales);
            setOptions(fiscales.slice(0, 50));
            setLoading(false);
            return;
          }
        }
        // si no filtramos por fiscal o no hubo datos, cargamos todos los operarios
        const result = await Operarios.listarOperarios();
        const todos = (result as Array<any>) || [];
        const normalizados = todos
          .filter((o: any) => { // aplicar filtro si se especificó un cargo distinto
            if (!props.filterCargo) return true;
            const cargo = String(o.cargo || '').toUpperCase().trim();
            return cargo === cargoFiltro;
          })
          .map((o: any) => ({
            id: String(o.legajo ?? o.id ?? ''),
            usuario: String(o.nombre ?? o.usuario ?? ''),
            cargo: String(o.cargo ?? ''),
          }))
          .filter((o: Operario) => o.id && o.usuario);
        setAllFiscales(normalizados);
        setOptions(normalizados.slice(0, 50));
      } catch {
        setAllFiscales([]);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    cargar();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterCargo]);

  // Modo evaluación: buscar en backend por legajo o nombre/apellido (q) y filtrar por tipoEvaluado
  useEffect(() => {
    if (!props.tipoEvaluado) return; // aplicar solo si se indicó tipoEvaluado
    if (props.disabled) { // si el input está deshabilitado
      setOptions([]); // limpiar opciones
      return; // salir sin consultar
    }
    const texto = (input || "").trim(); // normalizar texto de búsqueda
    if (texto.length < 1) { // exigir al menos 1 caracter para evitar listar masivo
      return; // salir
    }
    let activo = true; // flag para evitar setState si el componente se desmonta
    setLoading(true); // mostrar loading durante consulta
    Operarios.listarEvaluables({ tipo: props.tipoEvaluado, q: texto }) // llamar endpoint dedicado
      .then((arr: any[]) => { // procesar respuesta
        if (!activo) return; // evitar actualizar estado si ya no corresponde
        const normalizados = (arr || [])
          .map((o: any) => ({ // normalizar a estructura del componente
            id: String(o.legajo ?? o.id ?? ""), // legajo como id
            usuario: String(o.nombre ?? o.usuario ?? ""), // nombre como etiqueta
            cargo: String(o.cargo ?? ""), // cargo para referencia
          }))
          .filter((o: Operario) => o.id && o.usuario); // filtrar registros incompletos
        setOptions(normalizados.slice(0, 50)); // limitar para evitar listas enormes
      })
      .catch(() => { // ante error no bloquear UX
        if (!activo) return; // respetar flag
        setOptions([]); // dejar sin opciones
      })
      .finally(() => {
        if (!activo) return; // respetar flag
        setLoading(false); // finalizar loading
      });
    return () => { activo = false; }; // cleanup
  }, [input, props.tipoEvaluado, props.disabled]);

  // Filtrar en memoria según texto ingresado
  useEffect(() => {
    if (props.tipoEvaluado) return; // en modo evaluación el filtrado lo hace el backend
    const texto = (input || "").toLowerCase();
    const filtrados = texto
      ? allFiscales.filter((o) => ( // permitir búsqueda por nombre/apellido o por legajo
          o.usuario.toLowerCase().includes(texto) || // match por nombre
          String(o.id || "").toLowerCase().includes(texto) // match por legajo/id
        ))
      : allFiscales;
    setOptions(filtrados.slice(0, 50));
  }, [input, allFiscales]);

  const updateValue = (value: Operario | null) => {
    setValue(value);
    props.setValue(value ? value : null);
  };

  return (
    <MuiAutocomplete
      autoComplete
      className={props.className}
      classes={{ option: classes.option }}
      options={options}
      filterOptions={(options) => options}
      getOptionLabel={(option) => `${option.usuario} (${option.id})`} // mostrar nombre + legajo para facilitar selección
      getOptionSelected={(option, value) => option.id === value.id}
      value={value}
      onChange={(event, value) => updateValue(value)}
      inputValue={input}
      openOnFocus
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onInputChange={(event, value, reason) => {
        (reason !== "reset" || (reason === "reset" && value !== "")) &&
          setInput(value);
      }}
      disabled={props.disabled}
      loading={loading}
      loadingText="Cargando..."
      noOptionsText="Sin coincidencias"
      renderInput={(params) => (
        <TextField
          {...params}
          label={props.label}
          variant="outlined"
          error={props.error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default Autocompleteoperario;